import React, { useRef, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import './ImageUpload.css';

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, label = 'Header Image (Optional)' }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>(value || '');
  const [uploadMethod, setUploadMethod] = useState<'file' | 'url'>('file');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Update preview when value changes externally
  React.useEffect(() => {
    setPreviewUrl(value || '');
  }, [value]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (!supabase) {
      setUploadError('Storage niet beschikbaar');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { error: uploadErr } = await supabase.storage
        .from('job-images')
        .upload(fileName, file, {
          contentType: file.type,
          cacheControl: '31536000',
          upsert: false,
        });

      if (uploadErr) throw uploadErr;

      const { data: { publicUrl } } = supabase.storage
        .from('job-images')
        .getPublicUrl(fileName);

      setPreviewUrl(publicUrl);
      onChange(publicUrl);
    } catch (err: any) {
      setUploadError(err.message || 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setPreviewUrl(url);
    onChange(url);
  }, [onChange]);

  const handleRemove = useCallback(() => {
    setPreviewUrl('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="image-upload-container">
      <label className="image-upload-label">{label}</label>
      
      <div className="image-upload-method-toggle">
        <button
          type="button"
          className={`toggle-btn ${uploadMethod === 'file' ? 'active' : ''}`}
          onClick={() => setUploadMethod('file')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Upload File
        </button>
        <button
          type="button"
          className={`toggle-btn ${uploadMethod === 'url' ? 'active' : ''}`}
          onClick={() => setUploadMethod('url')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          Enter URL
        </button>
      </div>

      {uploadMethod === 'file' ? (
        <div
          className={`image-upload-dropzone ${isDragging ? 'dragging' : ''} ${previewUrl ? 'has-preview' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={!previewUrl && !uploading ? handleClick : undefined}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          {uploading && (
            <div className="image-upload-spinner">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinner-icon">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              <p className="upload-text-secondary">Uploading...</p>
            </div>
          )}

          {!uploading && uploadError && (
            <div className="image-upload-error">
              <p>{uploadError}</p>
              <button type="button" className="image-replace-btn" onClick={handleClick}>Opnieuw proberen</button>
            </div>
          )}

          {!uploading && !uploadError && previewUrl ? (
            <div className="image-preview-container">
              <img src={previewUrl} alt="Preview" className="image-preview" />
              <div className="image-preview-overlay">
                <button
                  type="button"
                  className="image-remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  title="Remove image"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className="image-replace-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                  title="Replace image"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  Replace
                </button>
              </div>
            </div>
          ) : (
            <div className="image-upload-placeholder">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="upload-icon">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <p className="upload-text">
                <span className="upload-text-primary">Click to upload</span> or drag and drop
              </p>
              <p className="upload-text-secondary">PNG, JPG, GIF up to 10MB</p>
              <p className="upload-text-secondary">Recommended: 120x120px or square</p>
            </div>
          )}
        </div>
      ) : (
        <div className="image-url-input">
          <input
            type="url"
            value={previewUrl}
            onChange={handleUrlChange}
            placeholder="https://example.com/image.jpg"
            className="image-url-field"
          />
          {previewUrl && (
            <div className="image-url-preview">
              <img src={previewUrl} alt="Preview" className="image-url-preview-img" />
              <button
                type="button"
                className="image-url-remove"
                onClick={handleRemove}
                title="Remove image"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;







